/**
 * LOGIN ログイン。Identity Platform の ID/パスワードと Google アカウントに対応する。
 *
 * ロールは認証後にサーバーが利用者マスタから判定する。画面でロールを選ばせることはしない。
 */

import { useState } from 'react';
import { useAuth } from '../auth/context.js';
import { AUTH_MODE } from '../auth/identity.js';

const DEV_ACCOUNTS = [
  { email: 'staff1@example.co.jp', label: '現場 花子（スタッフ）' },
  { email: 'approver1@example.co.jp', label: '承認 一郎（承認上長）' },
  { email: 'admin@example.co.jp', label: '管理 太郎（管理部）' },
];

export function LoginPage(): React.JSX.Element {
  const { signInWithPassword, signInWithGoogle, account, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (email.trim() === '' || (AUTH_MODE !== 'dev' && password === '')) {
      setError('入力してください');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'ログインできませんでした');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <p className="text-sm text-ink-muted">SEISUI</p>
          <h1 className="text-2xl font-bold">勤怠・日報システム</h1>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <h2 className="text-xl font-bold">ログイン</h2>
              <p className="text-sm text-ink-muted">
                会社から配布されたアカウントでログインしてください。
              </p>
            </div>

            {account && authError !== null && (
              <div className="notice notice--danger">
                <div>{authError}</div>
              </div>
            )}
            {error !== null && (
              <div className="notice notice--danger" role="alert">
                <div>{error}</div>
              </div>
            )}

            <form className="space-y-3" onSubmit={(event) => void submit(event)}>
              <label className="block">
                <span className="field-label">スタッフIDまたはメールアドレス</span>
                <input
                  className="input"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              {AUTH_MODE !== 'dev' && (
                <label className="block">
                  <span className="field-label">パスワード</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </label>
              )}
              <button type="submit" className="btn btn--primary w-full" disabled={busy}>
                ログイン
              </button>
            </form>

            {AUTH_MODE !== 'dev' && (
              <>
                <div className="flex items-center gap-3 text-[0.75rem] text-ink-muted">
                  <span className="h-px flex-1 bg-line" />
                  または
                  <span className="h-px flex-1 bg-line" />
                </div>
                <button
                  type="button"
                  className="btn btn--secondary w-full"
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    signInWithGoogle().catch((cause: unknown) =>
                      setError(cause instanceof Error ? cause.message : 'ログインできませんでした'),
                    );
                  }}
                >
                  Google アカウントでログイン
                </button>
              </>
            )}

            {AUTH_MODE === 'dev' && (
              <div className="notice notice--warn">
                <div>
                  <p className="font-semibold">開発モードで動作しています</p>
                  <p>メールアドレスだけでログインできます。本番環境では利用できません。</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {DEV_ACCOUNTS.map((item) => (
                      <button
                        key={item.email}
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => setEmail(item.email)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <p className="text-[0.75rem] text-ink-muted">
              パスワードを忘れた場合や、ログインできない場合は管理部にお問い合わせください。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
